import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { Copy, Link2, Share2, Unlink } from 'lucide-react';

import {
  createEventShareLink,
  getEventShareLink,
  revokeEventShareLink,
} from '../../utils/api';

const buttonSx = (colorKey) => (theme) => {
  const color = theme.palette[colorKey].main;
  return {
    m: 0,
    color,
    borderColor: color,
    boxShadow: `0 4px 0 ${darken(color, 0.58)}`,
    '&:hover': { color, borderColor: color, bgcolor: alpha(color, 0.12) },
    '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
  };
};

export default function EventSharePanel({ event }) {
  const [shareLink, setShareLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setShareLink(await getEventShareLink(event.id));
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'Unable to load the public link.');
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const ensureLink = async () => {
    if (shareLink?.share_url) return shareLink.share_url;
    const created = await createEventShareLink(event.id);
    setShareLink(created);
    return created.share_url;
  };

  const copy = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const url = await ensureLink();
      await navigator.clipboard.writeText(url);
      setMessage('Public event link copied.');
    } catch (copyError) {
      setError(copyError.data?.message || copyError.message || 'Unable to copy the public link.');
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const url = await ensureLink();
      if (navigator.share) {
        await navigator.share({ title: event.title, text: event.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setMessage('Public event link copied.');
      }
    } catch (shareError) {
      if (shareError.name !== 'AbortError') {
        setError(shareError.data?.message || shareError.message || 'Unable to share the event.');
      }
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Turn off this public event link? Anyone with it will lose access.')) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await revokeEventShareLink(event.id);
      setShareLink({ event_id: event.id, active: false, share_url: null });
      setMessage('Public event link turned off.');
    } catch (revokeError) {
      setError(revokeError.data?.message || revokeError.message || 'Unable to turn off the public link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack
      spacing={1.5}
      sx={(theme) => ({
        p: 2,
        border: `2px solid ${theme.palette.green.main}`,
        borderRadius: 4,
        bgcolor: theme.palette.background.paper,
      })}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Link2 size={20} />
        <Typography variant="h6" sx={{ color: 'primary.main' }}>Public link</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'secondary.main', textAlign: 'left' }}>
        Anyone with this link can view the event. Friends and attendees are never shown on the public page.
      </Typography>
      {message && <Alert severity="success" sx={{ color: 'green.main' }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <Button variant="outlined" startIcon={<Copy size={17} />} disabled={busy} onClick={copy} sx={buttonSx('primary')}>
          Copy link
        </Button>
        <Button variant="outlined" startIcon={<Share2 size={17} />} disabled={busy} onClick={share} sx={buttonSx('green')}>
          Share
        </Button>
        {shareLink?.active && (
          <Button variant="outlined" startIcon={<Unlink size={17} />} disabled={busy} onClick={revoke} sx={buttonSx('secondary')}>
            Turn off link
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
